import { useState } from 'react';
import PropTypes from 'prop-types';
import cn from "../../../utils/cn";
import Icon from "../Icon/Icon";
import { isKeyPressed } from "../../../utils/isKeyPressed";
import { RETURN_KEY_CODE, SPACEBAR_KEY_CODE } from "../../../utils/Browser/keyCodes";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const SECONDARY_TAG = 'span';
function Expandable(_ref) {
  let {
    children = null,
    className = '',
    header,
    headerTag = 'h5',
    id = '',
    secondary = false,
    initiallyCollapsed = false,
    expanded: controlledExpanded = null,
    onExpandChange = null
  } = _ref;
  const [expanded, setExpanded] = useState(controlledExpanded || !initiallyCollapsed);
  const HeaderTag = secondary ? SECONDARY_TAG : headerTag;
  const toggleExpansion = e => {
    e.stopPropagation();
    e.preventDefault();
    setExpanded(!expanded);
    if (onExpandChange) {
      onExpandChange();
    }
  };
  const onKeyDown = e => {
    // Capture Enter and Space keys
    if (isKeyPressed(e, 'Enter', RETURN_KEY_CODE) || isKeyPressed(e, ' ', SPACEBAR_KEY_CODE)) {
      e.preventDefault();
      toggleExpansion(e);
    }
  };
  const headerClass = () => cn({
    'py-3 px-4 hover:bg-medium-fill-grey': true,
    'bg-light-border-grey': !secondary,
    'border-b-2 border-light-border-grey text-xs': secondary,
    'text-xs uppercase font-heavy-font': headerTag === 'h5' && !secondary
  });
  const bodyClass = () => cn('py-3 px-4 text-sm', {
    hidden: !expanded,
    block: expanded,
    'border-b-2 border-light-border-grey': secondary
  });
  const chevronClass = () => cn({
    'float-right flex-none': true,
    'transform rotate-0': expanded,
    'transform rotate-180': !expanded
  });
  return (
    /*#__PURE__*/
    // eslint-disable-next-line react/jsx-props-no-spreading
    _jsx("section", {
      className: className,
      ...(id ? {
        id
      } : {}),
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx("button", {
          className: "w-full text-left text-text-blue",
          onClick: toggleExpansion,
          onKeyDown: onKeyDown,
          id: `${id}-button`,
          tabIndex: 0,
          "aria-expanded": expanded,
          type: "button",
          children: /*#__PURE__*/_jsx("div", {
            className: headerClass(secondary),
            children: /*#__PURE__*/_jsxs(HeaderTag, {
              className: "flex flex-no-wrap justify-between items-center",
              children: [/*#__PURE__*/_jsx("div", {
                className: "flex-auto font-heavy-font",
                children: header
              }), /*#__PURE__*/_jsx(Icon, {
                icon: "ChevronArrow",
                color: "medium-grey",
                size: 12,
                className: chevronClass(expanded)
              })]
            })
          })
        }), /*#__PURE__*/_jsx("div", {
          className: bodyClass(expanded, secondary),
          children: children
        })]
      })
    })
  );
}
Expandable.propTypes = {
  /** children for content inside */
  children: PropTypes.node,
  /** optional className for reference */
  className: PropTypes.string,
  /** Whether this card is expanded. */
  expanded: PropTypes.bool,
  /** string or node header used as the toggle for the main component */
  header: PropTypes.node,
  /** tag string used for semantic headings, it will not affect the styles */
  headerTag: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  /** optional id for reference */
  id: PropTypes.string,
  /** boolean to determine if container is rendered expanded */
  initiallyCollapsed: PropTypes.bool,
  /** function that is called when toggleExpansion is called */
  onExpandChange: PropTypes.func,
  /** determines style and color of expandable container */
  secondary: PropTypes.bool
};
export default Expandable;
//# sourceMappingURL=Expandable.js.map